import React, { useCallback, useEffect, useState } from 'react';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { format, parseISO } from 'date-fns/esm';
import { addHours } from 'date-fns';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import Button from '../../../components/Button';
import Loading from '../../../components/Loading';
import Paginacao from '../../../components/Paginacao';

import { optionsTipoModelo } from '../../../utils/options';
import { IModelo } from '..';
import getResponseErrors from '../../../utils/getResponseErrors';

export interface IPaginacao {
  termoBusca: string;
  limite: number;
  pagina: number;
}

type Props = {
  modeloMensagem?: boolean;
  showModal: boolean;
  setShowModal(value: boolean): void;
  setModeloSelecionado(model: IModelo): void;
};
const ListaModelos: React.FC<Props> = ({
  modeloMensagem = false,
  showModal,
  setShowModal,
  setModeloSelecionado,
}) => {
  const { addToast } = useToast();
  const [paginacao, setPaginacao] = useState<IPaginacao>({
    termoBusca: '',
    pagina: 1,
    limite: 20,
  });
  const [loading, setLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const [modelos, setModelos] = useState<IModelo[]>([]);

  useEffect(() => {
    async function buscaModelos(): Promise<void> {
      if (paginacao && !showModal) {
        try {
          setLoading(true);

          const parametroModelo = `&ModModeloMensagem=${modeloMensagem}`;

          const response = await api.get(
            `/modelo?pagina=${paginacao.pagina}&limite=${paginacao.limite}${parametroModelo}`,
          );

          const modelosApi: IModelo[] = response.data[0];

          const ModelosFormatado = modelosApi.map((model: IModelo) => {
            return {
              ...model,
              dataAtualizacaoFormatada: format(
                addHours(parseISO(model.updated_at.toString()), -3),
                'dd/MM/yyyy HH:mm:ss',
              ),
              nomeTipo:
                optionsTipoModelo.find(
                  op => op.value === model.ModTipo?.toString(),
                )?.label || '',
              ativoFormatado: model.ModAtivo ? 'Sim' : 'Não',
            };
          });

          setTotalRegistros(
            response.data[1] === 0 ? undefined : response.data[1],
          );

          setModelos(ModelosFormatado);
        } catch (error) {
          const mensagemErro = getResponseErrors(error);
          addToast({
            title: 'Erro ao carregar modelos',
            type: 'error',
            description: mensagemErro,
          });
          setTotalRegistros(0);
          setModelos([]);
        } finally {
          setLoading(false);
        }
      }
    }
    if (paginacao) buscaModelos();
  }, [paginacao, showModal, addToast, modeloMensagem]);

  const handleDeleteModelo = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        await api.delete(`/modelo/${codigo}`);
        setModelos(ModelosState =>
          ModelosState?.filter(model => model.ModCodigo !== codigo),
        );
        addToast({
          type: 'success',
          title: 'Modelo excluído',
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Erro ao deletar modelo',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  const handleEditar = useCallback(
    model => {
      setModeloSelecionado(model);
      setShowModal(true);
    },
    [setShowModal, setModeloSelecionado],
  );

  return (
    <>
      {loading && <Loading />}

      {!showModal &&
        (modelos && modelos.length > 0 ? (
          <>
            <table
              className="table table-hover table-sm table-font-small"
              style={{ borderWidth: '0px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Nome</th>
                  <th scope="col">Atualização</th>
                  <th scope="col">Colaborador</th>
                  {modeloMensagem === false && (
                    <>
                      <th scope="col">Tipo</th>
                      <th scope="col">Ativo</th>
                    </>
                  )}
                  <th scope="col">&nbsp;</th>
                  <th scope="col">&nbsp;</th>
                </tr>
                {modelos.map((model: IModelo) => (
                  <tr
                    key={`Modelo-${model.ModCodigo}`}
                    style={{ fontSize: 'Small', height: '30px' }}
                    className={`${!model.ModAtivo ? 'text-muted' : ''}`}
                  >
                    <td>{model.ModNome}</td>
                    <td>{model.dataAtualizacaoFormatada}</td>
                    <td>{model.usuarioEditor.UsuNome}</td>
                    {modeloMensagem === false && (
                      <>
                        <td style={{ width: '100px' }}>{model.nomeTipo}</td>
                        <td style={{ width: '60px' }}>
                          {model.ativoFormatado}
                        </td>
                      </>
                    )}
                    <td className="p-0" style={{ width: '40px' }}>
                      <Button
                        type="button"
                        variant="primary-outline"
                        className="py-0"
                        title={model.ModCodigo.toString()}
                        onClick={() => handleEditar(model)}
                      >
                        <FaPencilAlt />
                      </Button>
                    </td>

                    <td className="p-0" style={{ width: '40px' }}>
                      {!modeloMensagem && (
                        <Button
                          type="button"
                          variant="secondary-outline"
                          className="py-0"
                          title={model.ModCodigo.toString()}
                          onClick={() => {
                            handleDeleteModelo(model.ModCodigo);
                          }}
                        >
                          <FaTrashAlt />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginacao
              limitePagina={paginacao?.limite || 15}
              paginasVizinhas={2}
              totalRegistros={totalRegistros}
              paginaInicio={paginacao?.pagina}
              handleProximaPagina={handleProximaPagina}
            />
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>Nenhum registro encontrado.</h5>
          </div>
        ))}
    </>
  );
};

export default ListaModelos;
